import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import * as apicall from "../../../components/Apicall";
import * as inventoryService from "../../../services/inventoryService";
import * as estimationService from "../../../services/estimationService";
import { describeConflict } from "../../../services/resourceService";

// Creating a brand-new booking and taking payment, in the stepper's last step. This is the
// body of Checkout.jsx's handleConfirm (and its helpers) moved into a hook, unchanged in
// behaviour, so the booking no longer needs a separate page:
//   - Cash: the booking is created straight away with the chosen payment status.
//   - Pay Online: Stripe (non-INR) creates the booking as pending and redirects to the hosted
//     page; Razorpay (INR) opens its modal and creates the booking once paid.
// Checkout.jsx itself stays - it is the clinic form's checkout and the return page of a
// cancelled Stripe payment - so the keys below are shared with it.

export const CHECKOUT_STATE_KEY = "utilstop_checkout_state";
export const PENDING_GID_KEY = "utilstop_checkout_pending_gid";

export const PAYMENT_METHODS_INR = [
  { id: "cash", label: "Cash", emoji: "💵", offline: true },
  { id: "online", label: "Pay Online", emoji: "🔗", offline: false }
];
export const PAYMENT_METHODS_NON_INR = [
  { id: "cash", label: "Cash", emoji: "💵", offline: true },
  { id: "online", label: "Pay Online", emoji: "🔗", offline: false }
];
export const paymentMethodsFor = (currency) => (currency === "INR" ? PAYMENT_METHODS_INR : PAYMENT_METHODS_NON_INR);

// "New booking" always starts In Progress (2); the Status dropdown only applies when editing.
export const NEW_BOOKING_STATUS = 2;

// The server saves a booking even when its optional resource is already in use at that
// time, and reports the clash in the response. Surface it; it never blocks anything.
const warnResourceClash = (bookingRes) => {
  if (bookingRes?.resource_conflicts) {
    toast.warn(
      `Booked, but the selected resource is also in use at this time: ${describeConflict(bookingRes.resource_conflicts)}`
    );
  }
};

// Persists parts staged on the form (priceSection rows tagged isPart) now that a real
// booking row id finally exists. A failed part must never block/roll back the booking, which
// by this point is already created (and, for online payments, already paid for) - just warn.
export const flushStagedParts = async (rowId, submittedPriceSection) => {
  const staged = (submittedPriceSection || []).filter((r) => r?.isPart);
  if (!rowId || staged.length === 0) return;
  const results = await Promise.allSettled(
    staged.map((p) =>
      inventoryService.addBookingPart(rowId, {
        item_id: p.item_id,
        quantity: p.quantity,
        unit_price: p.unit_price
      })
    )
  );
  const failed = results
    .map((r, i) => ({ r, name: staged[i].name }))
    // apicall.post resolves the parsed body even on a 4xx rather than throwing - a business
    // rule failure looks like {success:false}, a 422 like {errors:[...]} with no `success`.
    .filter(
      ({ r }) =>
        r.status === "rejected" ||
        r.value?.success === false ||
        (Array.isArray(r.value?.errors) && r.value.errors.length > 0)
    )
    .map(({ name }) => name);
  if (failed.length > 0) {
    toast.warn(`Booking saved, but could not attach: ${failed.join(", ")}. Add manually via Edit Booking.`);
  }
};

// Flags the source estimation CONVERTED once its booking exists. A failure must never roll
// back the booking - just warn so the vendor can reconcile the quotation manually.
export const markEstimationConverted = async (fromEstimation, rowId) => {
  if (!fromEstimation || !rowId) return;
  try {
    const res = await estimationService.markConverted(fromEstimation, rowId);
    if (!res.success) throw new Error(res.message);
  } catch (err) {
    toast.warn(`Booking saved, but the source quotation could not be marked converted: ${err.message || err}`);
  }
};

// Service Specifics (asset + complaint/action-taken note) lives in its own table; for a new
// booking it could only be saved once the booking (and customer) exist.
export const flushServiceSpecifics = async (bookingValues, rowId, customerId) => {
  if (!rowId) return;
  let { customer_asset_id, customer_complaint, action_taken, new_asset_draft } = bookingValues || {};
  if (new_asset_draft && customerId) {
    // A brand-new customer's asset couldn't be created until now.
    try {
      const { label, field_values, custom_fields, asset_type_id } = new_asset_draft;
      const res = await apicall.post(
        { customer_id: customerId, asset_type_id, label, field_values, custom_fields },
        "ezp_customer_assets"
      );
      if (res?.success) customer_asset_id = res.data.id;
      else
        toast.warn(
          `Booking saved, but the details could not be saved: ${
            res?.errors?.[0]?.msg || res?.message || "please add them again from the customer profile."
          }`
        );
    } catch (e) {
      console.log(e);
    }
  }
  if (!customer_asset_id && !customer_complaint && !action_taken) return;
  try {
    await apicall.post({ booking_id: rowId, customer_asset_id, customer_complaint, action_taken }, "ezp_booking_service_details");
  } catch (e) {
    console.log(e);
  }
};

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const groupIdOf = (bookingRes) => Number(bookingRes?.booking_id?.split?.("_")?.pop?.()) || null;

/**
 * @param partners / partnerId   the vendor's profiles; a "utilstop" profile posts to create_partner_booking
 * @param currency               "INR" -> Razorpay, otherwise Stripe
 * @param returnPath             where Checkout.jsx's Back should land after a cancelled Stripe payment
 * @param onBooked(bookingRes, bookingValues)   the booking exists and everything was attached
 */
export default function useBookingCheckout({ partners, partnerId, currency, returnPath = "/vendor_bookings", onBooked }) {
  const [submitting, setSubmitting] = useState(false);
  const isINR = currency === "INR";

  const apiName = useCallback(() => {
    const selectedPartner = (partners || []).find((p) => p.id === Number(partnerId));
    return selectedPartner?.partner_name?.trim().toLowerCase() === "utilstop" ? "create_partner_booking" : "create_ezp_booking";
  }, [partners, partnerId]);

  const afterCreated = useCallback(
    async (bookingRes, payload, bookingValues) => {
      warnResourceClash(bookingRes);
      await flushStagedParts(bookingRes.ezp_row_id, payload.priceSection);
      await markEstimationConverted(bookingValues?.from_estimation, bookingRes.ezp_row_id);
      await flushServiceSpecifics(bookingValues, bookingRes.ezp_row_id, bookingRes.customer_id);
    },
    []
  );

  const finish = useCallback(
    (bookingRes, bookingValues) => {
      sessionStorage.removeItem(PENDING_GID_KEY);
      sessionStorage.removeItem(CHECKOUT_STATE_KEY);
      window.dispatchEvent(new Event("setupCompletedUpdated"));
      onBooked?.(bookingRes, bookingValues);
    },
    [onBooked]
  );

  /**
   * @param bookingValues      the form payload (see bookingPayload.buildSubmitValues)
   * @param paymentMethod      "cash" | "online"
   * @param paidAmount         what the customer pays now
   * @param cashPaymentStatus  ezp_payment_status id used for cash (falls back to 1 = Pending)
   * @param total              grand total, charged in full for online payments
   */
  const createBooking = useCallback(
    async ({ bookingValues, paymentMethod, paidAmount, cashPaymentStatus, total }) => {
      if (!paymentMethod) {
        toast.error("Please select a payment method");
        return null;
      }
      setSubmitting(true);
      // A fresh attempt must not inherit a previous online attempt's pending booking id.
      sessionStorage.removeItem(PENDING_GID_KEY);
      sessionStorage.removeItem(CHECKOUT_STATE_KEY);
      let pendingGid = null;

      const buildPayload = (extra = {}) => ({
        ...bookingValues,
        paid_amount: paidAmount,
        payment_method: paymentMethod,
        ...(pendingGid ? { group_id: pendingGid } : {}),
        ...extra
      });
      const persistGroupId = (id) => {
        pendingGid = id;
        sessionStorage.setItem(PENDING_GID_KEY, String(id));
      };

      try {
        const method = paymentMethodsFor(currency).find((m) => m.id === paymentMethod);
        const name = apiName();

        if (method?.offline) {
          // Cash: the payment status is whatever the vendor picked (Pending by default).
          const payload = buildPayload({ status: bookingValues.status || NEW_BOOKING_STATUS, payment: cashPaymentStatus || 1 });
          const bookingRes = await apicall.post(payload, name);
          const gid = groupIdOf(bookingRes);
          if (!gid) {
            // Surface the backend's specific reason instead of a generic message.
            toast.error(bookingRes?.message || "Booking creation failed.");
            setSubmitting(false);
            return null;
          }
          persistGroupId(gid);
          await afterCreated(bookingRes, payload, bookingValues);
          toast.success("Booking confirmed!");
          finish(bookingRes, bookingValues);
          setSubmitting(false);
          return bookingRes;
        }

        // ── Online ──
        if (!isINR) {
          // Stripe: create the booking as pending, then redirect to the hosted page.
          const payload = buildPayload({ status: bookingValues.status || NEW_BOOKING_STATUS, payment: 1 });
          const bookingRes = await apicall.post(payload, name);
          pendingGid = groupIdOf(bookingRes);
          if (!pendingGid) {
            toast.error(bookingRes?.message || "Booking creation failed.");
            setSubmitting(false);
            return null;
          }
          persistGroupId(pendingGid);
          await afterCreated(bookingRes, payload, bookingValues);
        }

        let orderRes;
        try {
          orderRes = await apicall.post(
            {
              amount: Number(total || 0).toFixed(2),
              currency,
              group_id: pendingGid,
              success_return_url: window.location.origin + "/payments",
              cancel_return_url: window.location.origin + "/checkout"
            },
            "create_ezp_payment_order"
          );
        } catch (err) {
          toast.error("Payment order failed: " + (err?.message || "Unknown error"));
          setSubmitting(false);
          return null;
        }
        if (!orderRes?.success) {
          toast.error("Could not create payment order: " + (orderRes?.message || "Unknown error"));
          setSubmitting(false);
          return null;
        }

        if (orderRes.payment_gateway === "stripe") {
          // Saved before the full-page redirect: Checkout.jsx restores it if the customer cancels.
          try {
            sessionStorage.setItem(
              CHECKOUT_STATE_KEY,
              JSON.stringify({ bookingValues, partners, partner_id: partnerId, returnPath })
            );
          } catch (e) {
            /* storage full / blocked: the redirect still works */
          }
          window.location.href = orderRes.session_url;
          return null;
        }

        // Razorpay (INR): their modal opens, the booking is created once it is paid.
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error("Failed to load Razorpay SDK. Check internet connection.");
          setSubmitting(false);
          return null;
        }
        const rzp = new window.Razorpay({
          key: orderRes.key,
          amount: orderRes.amount,
          currency: orderRes.currency,
          name: "UtilStop",
          description: "Service Booking",
          order_id: orderRes.order_id,
          prefill: {
            name: bookingValues.userName || "",
            contact: bookingValues.phoneNumber || "",
            email: bookingValues.email || ""
          },
          theme: { color: "#d01c4c" },
          handler: async (response) => {
            try {
              const payload = buildPayload({
                status: bookingValues.status || NEW_BOOKING_STATUS,
                payment: 2, // "Paid Online"
                invoice_id: response.razorpay_payment_id,
                payment_gateway: "razorpay",
                paid_amount: Number(total || 0).toFixed(2) // online: the full amount was charged
              });
              const bookingRes = await apicall.post(payload, name);
              const gid = groupIdOf(bookingRes);
              if (!gid) {
                // The payment already went through, so this is not a simple "try again".
                toast.error(
                  "Payment was successful, but booking creation failed: " +
                    (bookingRes?.message || "Unknown error") +
                    ". Please contact support with your payment reference."
                );
                return;
              }
              persistGroupId(gid);
              await afterCreated(bookingRes, payload, bookingValues);
              toast.success("Payment successful! Booking confirmed.");
              finish(bookingRes, bookingValues);
            } catch (e) {
              toast.error("Payment verification failed. Contact support.");
            } finally {
              setSubmitting(false);
            }
          },
          modal: {
            ondismiss: () => {
              setSubmitting(false);
              toast.info("Payment cancelled.");
            }
          }
        });
        rzp.open();
        return null;
      } catch (err) {
        setSubmitting(false);
        toast.error(err?.message || "Something went wrong");
        return null;
      }
    },
    [afterCreated, apiName, currency, finish, isINR, partnerId, partners, returnPath]
  );

  return { submitting, createBooking };
}
